import { CircleHelp } from "lucide-react";
import {
  DESCRIPTION_INPUT_CLASS,
  FieldLabel,
  FormSection,
} from "./descriptionUi";
import {
  COMMISSION_TYPE,
  COMMISSION_TYPE_OPTIONS,
  emptyCommissionForm,
  type PropertyCommissionErrors,
  type PropertyCommissionForm,
} from "../../utils/propertyCommission";

type Props = {
  value: PropertyCommissionForm;
  errors?: PropertyCommissionErrors;
  onChange: (next: PropertyCommissionForm) => void;
};

function PercentField({
  id,
  label,
  tooltip,
  value,
  error,
  onChange,
}: {
  id: string;
  label: string;
  tooltip: string;
  value: string;
  error?: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="min-w-0">
      <FieldLabel htmlFor={id} error={error}>
        <span className="inline-flex items-center gap-1">
          {label}
          <CircleHelp
            className="h-3.5 w-3.5 text-slate-400"
            aria-label={tooltip}
            title={tooltip}
          />
        </span>
      </FieldLabel>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Ex: 10.50"
        className={`${DESCRIPTION_INPUT_CLASS} w-full`}
      />
    </div>
  );
}

export default function CommissionSection({ value, errors, onChange }: Props) {
  const type = value.commission_type;

  return (
    <FormSection
      title="Comissões e ADM"
      description="Como este imóvel é gerenciado para cálculo de receitas."
    >
      <FieldLabel>Como é gerenciado este imóvel?</FieldLabel>
      <div className="flex flex-col gap-2">
        {COMMISSION_TYPE_OPTIONS.map((option) => {
          const selected = type === option.value;
          return (
            <label
              key={option.value}
              className={`flex cursor-pointer items-start gap-2 rounded-md border px-3 py-2 text-sm ${
                selected
                  ? "border-slate-800 bg-slate-50 text-slate-900"
                  : "border-slate-200 bg-white text-slate-700"
              }`}
            >
              <input
                type="radio"
                name="commission_type"
                className="mt-0.5"
                checked={selected}
                onChange={() => onChange(emptyCommissionForm(option.value))}
              />
              <span>{option.label}</span>
            </label>
          );
        })}
      </div>

      {type === COMMISSION_TYPE.ADMIN_7CANTOS ? (
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <PercentField
            id="admin_percentage"
            label="Porcentagem de ADM mensal (%)"
            tooltip="Taxa mensal que a 7Cantos cobra pelo gerenciamento"
            value={value.admin_percentage}
            error={errors?.admin_percentage}
            onChange={(admin_percentage) =>
              onChange({ ...value, admin_percentage })
            }
          />
          <PercentField
            id="first_rent_commission_7cantos_percentage"
            label="Comissão 7Cantos no 1º aluguel (%)"
            tooltip="Porcentagem que a 7Cantos retém da comissão do primeiro aluguel"
            value={value.first_rent_commission_7cantos_percentage}
            error={errors?.first_rent_commission_7cantos_percentage}
            onChange={(first_rent_commission_7cantos_percentage) =>
              onChange({ ...value, first_rent_commission_7cantos_percentage })
            }
          />
        </div>
      ) : null}

      {type === COMMISSION_TYPE.PARTNERSHIP ? (
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="min-w-0">
            <FieldLabel htmlFor="partner_name" error={errors?.partner_name}>
              Nome do parceiro
            </FieldLabel>
            <input
              id="partner_name"
              type="text"
              maxLength={255}
              value={value.partner_name}
              onChange={(event) =>
                onChange({ ...value, partner_name: event.target.value })
              }
              placeholder="Ex: Imobiliária ABC"
              className={`${DESCRIPTION_INPUT_CLASS} w-full`}
            />
          </div>
          <PercentField
            id="partner_first_rent_commission_percentage"
            label="Comissão do parceiro no 1º aluguel (%)"
            tooltip="Porcentagem que o parceiro retém da comissão do primeiro aluguel"
            value={value.partner_first_rent_commission_percentage}
            error={errors?.partner_first_rent_commission_percentage}
            onChange={(partner_first_rent_commission_percentage) =>
              onChange({ ...value, partner_first_rent_commission_percentage })
            }
          />
        </div>
      ) : null}
    </FormSection>
  );
}
